import { useContext } from "react";

import { Box, Column, IconButton, Tooltip } from "@hightouch/htui";
import {
  MiniNavSidebarContext,
  SIDEBAR_SHORTCUT,
  SIDEBAR_TOGGLE_PROPS,
} from "src/components/layout/nav/mini-nav/mini-nav-context";
import { useIsQuietlyFocused } from "src/components/layout/nav/nav-focus";
import { useNavWidth } from "src/contexts/nav-width-context";
import { SmallLogo } from "./logos";
import { SidebarOpenIcon, SidebarClosedIcon } from "src/ui/icons";
import { useUser } from "src/contexts/user-context";
import * as analytics from "src/lib/analytics";
import { RouterLink } from "src/router";

/**
 * Button to toggle the sidebar navigation.
 *
 * When nav is collapsed, the button displays the small hightouch logomark
 * and on hover of itself, or over the collapsed nav deadspace, the sidebar icon is shown in place of the logomark.
 *
 * On sm and md screens, you cannot expand the sidebar,
 * and the logomark acts as a home link instead.
 */
export function NavToggleButton({
  showHoverState = false,
}: {
  showHoverState?: boolean;
}) {
  const { canNavOpen, isNavOpen, setIsNavOpen } = useNavWidth();
  const { workspace, user } = useUser();
  const miniNav = useContext(MiniNavSidebarContext);
  const quietFocus = useIsQuietlyFocused();

  const orgLogo = user?.organization?.logo_url;

  // The phone's sheet closes on Escape, the scrim, or a navigation too, and this shows how without a keyboard.
  if (miniNav?.overlay === "sheet") {
    return (
      <IconButton
        aria-label="Close sidebar"
        icon={SidebarOpenIcon}
        {...SIDEBAR_TOGGLE_PROPS}
        flexShrink={0}
        onClick={(event) => miniNav.toggleDocked("toggle", event.detail === 0)}
      />
    );
  }
  // The peeked sidebar has no toggle of its own: the crumb bar's, which opened it, docks it.
  if (miniNav?.overlay === "peek") return null;
  // Docked, the toggle shows only on keyboard focus, as the first stop in the nav; `[` or the edge collapses it too.
  if (miniNav) {
    return (
      <Tooltip
        message="Collapse sidebar"
        keyboardShortcut={SIDEBAR_SHORTCUT}
        placement="right"
        // Shut when docking from the keyboard lands focus here; the viewer's own Tab still shows it.
        isDisabled={quietFocus.isQuiet}
        keepMountedWhileDisabled
      >
        <IconButton
          data-nav-toggle
          aria-label="Collapse sidebar"
          icon={SidebarOpenIcon}
          {...SIDEBAR_TOGGLE_PROPS}
          position="absolute"
          // Over the inbox at the header's end, on the nav's background, so the switcher's name and chevron stay clear.
          right={0}
          zIndex={1}
          opacity={0}
          pointerEvents="none"
          // Replaces the theme's focus style, so it restates the ring.
          _focusVisible={{
            opacity: 1,
            pointerEvents: "auto",
            boxShadow: "outline",
            bg: "base.background",
          }}
          onFocus={quietFocus.onFocus}
          onBlur={quietFocus.onBlur}
          // The fold tracks nav_docked and nav_undocked itself.
          onClick={(event) => {
            // A press with no pointer position is a keyboard's.
            miniNav.toggleDocked("toggle", event.detail === 0);
          }}
        />
      </Tooltip>
    );
  }

  function handleClick() {
    if (canNavOpen) {
      analytics.track("Navigation: Toggle sidebar", {
        workspace_id: workspace?.id,
        nav_state: isNavOpen ? "closed" : "open",
      });
      setIsNavOpen((prev) => !prev);
    }
  }

  // Nav cannot be toggled on mobile screens.
  // Display the logomark as a home link instead
  if (!canNavOpen) {
    return (
      <RouterLink to="/" aria-label="Home">
        <SmallLogo />
      </RouterLink>
    );
  }

  return (
    <Tooltip
      message={`${isNavOpen ? "Close" : "Open"} sidebar`}
      keyboardShortcut="mod+/"
      placement="right"
    >
      <Column
        as="button"
        data-nav-toggle
        aria-label={`${isNavOpen ? "Close" : "Open"} navigation sidebar`}
        className="group"
        boxSize={orgLogo && !isNavOpen ? 10 : 8}
        borderRadius="lg"
        color="text.tertiary"
        align="center"
        justify="center"
        flexShrink={0}
        userSelect="none"
        position="relative"
        transitionProperty="background-color, color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _hover={{
          bg: "gray.200",
          color: "text.tertiary",
        }}
        _active={{
          bg: "transparent", // Avoid hover state bg flash when toggling nav
        }}
        _focusVisible={{
          outline: "none",
          boxShadow: "outline",
        }}
        onClick={handleClick}
      >
        {isNavOpen ? (
          <Box as={SidebarOpenIcon} boxSize={5} flexShrink={0} />
        ) : (
          <Column align="center" justify="center" pos="absolute" inset={0}>
            <Box
              as={SidebarClosedIcon}
              position="absolute"
              color="inherit"
              flexShrink={0}
              boxSize={5}
              showArrow={showHoverState}
              transitionProperty="opacity"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              opacity={showHoverState ? 1 : 0}
              visibility={showHoverState ? "visible" : "hidden"}
              _groupHover={{
                opacity: 1,
                visibility: "visible",
              }}
            />

            <SmallLogo
              position="absolute"
              flexShrink={0}
              transitionProperty="opacity"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              opacity={showHoverState ? 0 : 1}
              visibility={showHoverState ? "hidden" : "visible"}
              _groupHover={{
                opacity: 0,
                visibility: "hidden",
              }}
            />
          </Column>
        )}
      </Column>
    </Tooltip>
  );
}
